<script>

  import HomepageBooks from '@/components/HomepageBooks'
  import FlexibleContent from '@/components/FlexibleContent'
  import CallToActionBlocks from '~/components/FlexibleContent/CallToActionBlocks.vue'
  import { HeadMeta } from '@/utils/head-meta'
  import { gtmSendEvent } from '@/utils/gtm-send-event'
  import ProductsPerSchoolLevel from '@/components/ProductsPerSchoolLevel.vue'
  import HomeCarousel from '~/components/HomeCarousel.vue'
  import toRelativeUrl from '@/mixins/toRelativeUrl'

  export default {
    components: {
      HomepageBooks,
      FlexibleContent,
      CallToActionBlocks,
      ProductsPerSchoolLevel,
      HomeCarousel
    },
    mixins: [toRelativeUrl],
    async asyncData ({ app }) {
      const { items } = await app.$contentful.getEntries({
        content_type: 'page',
        'fields.template': 'Homepage',
        include: 2
      })
      return {
        page: items[0]
      }
    },
    head () {
      return HeadMeta(this.page.fields)
    },
    computed: {
      // Internal links render as <nuxt-link> for SPA nav; absolute or external
      // URLs must render as <a>, since vue-router rejects non-path :to values.
      productTypeLinks () {
        return (this.page?.fields?.productTypes ?? []).map((item) => {
          const resolved = this.toRelativeUrl(item.fields.link)
          const isInternal = typeof resolved === 'string' && resolved.startsWith('/')
          return isInternal
            ? { item, tag: 'nuxt-link', attrs: { to: resolved } }
            : { item, tag: 'a', attrs: { href: resolved, target: '_blank', rel: 'noopener noreferrer' } }
        })
      }
    },
    methods: {
      trackEvent () {
        gtmSendEvent('home', 'products-cta', 'clicked')
      }
    }
  }
</script>

<template>
  <article class="view view-home">
    <header class="bg-blue-100 view-header md:pb-10 ">
      <div class="container relative px-4 md:px-8">
        <client-only>
          <homepage-books v-if="!$device.isMobile" />
        </client-only>
        <div class="w-full mx-auto md:w-10/12 xl:w-8/12">
          <animated-title>
            <h1 class="pb-4 text-3xl animate md:text-5xl md:text-center">
              {{ page.fields.title }}
            </h1>
          </animated-title>
          <div v-if="page.fields.productTypes">
            <div
              class="gap-2 lg:gap-4"
              :class="page.fields.productTypes.length < 4 ? 'flex justify-center flex-wrap' : 'grid grid-cols-2 lg:grid-cols-4'"
            >
              <component
                :is="link.tag"
                v-for="(link, index) in productTypeLinks"
                :key="index"
                v-bind="link.attrs"
                :class="page.fields.productTypes.length < 4 ? 'w-1/4 ' : ''"
              >
                <div class="relative product-type-block h-full ">
                  <span
                    v-if="link.item.fields && link.item.fields.discountText"
                    class="float-right font-bold px-1 py-1.5 text-xs rounded-sm text-blue-500 bg-blue-100 inline"
                  >{{ link.item.fields.discountText }}</span>
                  <span
                    v-else
                    class="px-1 py-1.5"
                  />
                  <img
                    :src="link.item.fields.image.fields.file.url"
                    :alt="link.item.fields.title"
                  >
                  <h3 class="my-3 font-semibold text-1xl">
                    {{ link.item.fields.title }}
                  </h3>
                  <span class="text-base text-gray-500 block">
                    {{ link.item.fields.description }}
                  </span>
                </div>
              </component>
            </div>
            <div class="flex justify-center mt-4 text-center pb-7 align-center">
              <e-button
                tag="nuxt-link"
                to="/producten"
                type="quinary"

                @click.native="trackEvent()"
              >
                Alle producten
              </e-button>
            </div>
          </div>
        </div>
      </div>
    </header>
    <call-to-action-blocks :data="page.fields.contentBlocksBefore" />
    <products-per-school-level />
    <flexible-content :content="page.fields.content" />
    <home-carousel :content="page.fields.tools" />
  </article>
</template>

<style lang="scss">
.view-home {
  .rich-text {
    &:first-of-type {
      @apply text-lg;

      @screen md {
        @apply pb-10;
      }
    }
  }

  .articles-carousel {
    @apply mt-4;
    @screen md {
      @apply mt-40;
    }
  }
  .product-type-block {
    @apply bg-white p-4 mb-4 rounded-sm border border-gray-150;
  }
}
</style>
